import { DiscoveryLevel, FeatureType, SettingStructure, SettingsChangeEvent, ShareSettings } from '../../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';
import { isDefined, undefinedIfEmpty } from '../../helpers/utils';

import { StateController } from '../../helpers/state/state-management/state-controller';
import featuresInfo from '../../helpers/state/features-info';
import { logWithAmplitude } from '../../helpers/api/api';
import topicData from '../../helpers/state/topic-data';

@customElement('admin-share-settings-fragment')
export class AdminShareSettings extends LitElement {
    amplitude_callsite: string = 'Admin Share Settings Fragment';

    @property({ type: Boolean })
    open: boolean = false;

    @state()
    shareSettingsList: SettingStructure[] = [];

    @state()
    analyicsSettingsList: SettingStructure[] = [];

	private readonly bindTopicState = new StateController(this, topicData);

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {

        this.shareSettingsList = [
            {
                fieldName: 'page_title',
                title: 'Page SEO Title',
                description: 'The title of the page that will be shared in search engines and social media. If blank, the map name will be used.',
                inputType: 'inputShort',
                textCharLimit: 100,
                fieldType:'string',
                initialValueString: topicData.topic?.share_settings?.page_title ?? topicData.topic?.name,
                widthPercentage: 100,
                isHidden: false,
            },
            {
                fieldName: 'page_description',
                title: 'Page SEO Description',
                description: 'The description of the page that will be shared in search engines and social media. If blank, the map description will be used. This is different from the map description.',
                inputType: 'inputLong',
                textCharLimit: 200,
                fieldType:'string',
                initialValueString: topicData.topic?.share_settings?.page_description ?? topicData.topic?.description,
                widthPercentage: 100,
                isHidden: false,
            },
            {
                fieldName: 'page_image',
                title: 'Page SEO Image',
                description: 'The image that will be shared in search engines and social media. If blank, the map custom social media image will be used.',
                inputType: 'image',
                fieldType:'string',
                initialValueString: topicData.topic?.share_settings?.page_image ?? topicData.topic?.custom_social_media_image,
                widthPercentage: 100,
                isHidden: false,
            },

            {
                fieldName: 'favicon',
                title: 'Favicon (16px x 16px)',
                description: 'The favicon for the map. The size should be 16x16 pixels. If blank, the default favicon will be used.',
                inputType: 'image',
                fieldType:'string',
                initialValueString: topicData.topic?.share_settings?.favicon ?? topicData.categories[0]?.icon,
                widthPercentage: 100,
                isHidden: false,
            },
            {
                fieldName: 'phone_short_name',
                title: 'Phone Short Name (12 Characters)',
                description: 'The short name for the map when saved to the home screen on mobile devices. This will be used as the name of the app icon. If blank, the map name will be used.',
                inputType: 'inputShort',
                textCharLimit: 12,
                fieldType:'string',
                initialValueString: topicData.topic?.share_settings?.phone_short_name ?? topicData.topic?.name.slice(0, 12),
                widthPercentage: 100,
                isHidden: false,
            },
            {
                fieldName: 'phone_icon_large',
                title: 'Phone Icon Large (512px x 512px)',
                description: 'The large icon for the map when saved to the home screen on mobile devices. Size should be 512x512 pixels and in png.',
                inputType: 'image',
                fieldType:'string',
                initialValueString: topicData.topic?.share_settings?.phone_icon_large,
                widthPercentage: 100,
                isHidden: false,
            },
            {
                fieldName: 'phone_icon_small',
                title: 'Phone Icon Small (192px x 192px)',
                description: 'The small icon for the map when saved to the home screen on mobile devices. Size should be 192x192 pixels and in png.',
                inputType: 'image',
                fieldType:'string',
                initialValueString: topicData.topic?.share_settings?.phone_icon_small,
                widthPercentage: 100,
                isHidden: false,
            }
        ];
        this.analyicsSettingsList = [
            {
                fieldName: 'google_tag',
                title: 'Google Tag (GTM or GA)',
                description: 'Enter your Google Tag Manager ID (e.g., GTM-XXXXXXX) or Google Analytics 4 ID (e.g., G-XXXXXXXXXX). This tag will be injected into the map-viewing page and challenge page (if applicable) for analytics tracking.',
                inputType: 'inputShort',
                textCharLimit: 50,
                fieldType:'string',
                initialValueString: topicData.topic?.share_settings?.google_tag,
                widthPercentage: 100,
                isHidden: false,
            },
        ];
    }

    async onURLChange(e: CustomEvent<SettingsChangeEvent>) {
        const newUpdatedValue = e.detail.updatedValue;
        const fieldToUpdate = e.detail.field;

        if (isDefined(e.detail.callback)) {
            await e.detail.callback();
        }

        await topicData.fetchAndUpdateTopicData({
            [fieldToUpdate]: newUpdatedValue
        }, this.amplitude_callsite);
    }

    async hasChange(e: CustomEvent) {
        let newUpdatedValue = e.detail.updatedValue;
        let fieldToUpdate = e.detail.field;
        const shareSettings: ShareSettings = topicData.topic?.share_settings ?? {
            page_title: undefined,
            page_description: undefined,
            page_image: topicData.topic?.custom_social_media_image,
            phone_short_name: undefined,
            phone_icon_large: undefined,
            phone_icon_small: undefined,
            favicon: undefined,
            google_tag: undefined,
        };

        newUpdatedValue = undefinedIfEmpty(newUpdatedValue);

        if (fieldToUpdate === 'page_title') {
            shareSettings.page_title = newUpdatedValue;
        } else if (fieldToUpdate === 'page_description') {
            shareSettings.page_description = newUpdatedValue;
        } else if (fieldToUpdate === 'page_image') {
            // if it starts with /i/ we need to give it a full URL
            if (newUpdatedValue.startsWith('/i/') === true) {
                newUpdatedValue = `${window.location.origin}${newUpdatedValue as string}`;
            }
            shareSettings.page_image = newUpdatedValue;
        } else if (fieldToUpdate === 'phone_short_name') {
            shareSettings.phone_short_name = newUpdatedValue;
        } else if (fieldToUpdate === 'phone_icon_large') {
            // if it starts wtih /i/ we need to give it a full URL
            if (newUpdatedValue.startsWith('/i/') === true) {
                newUpdatedValue = `${window.location.origin}${newUpdatedValue as string}`;
            }
            shareSettings.phone_icon_large = newUpdatedValue;
        } else if (fieldToUpdate === 'phone_icon_small') {
            // if it starts wtih /i/ we need to give it a full URL
            if (newUpdatedValue.startsWith('/i/') === true) {
                newUpdatedValue = `${window.location.origin}${newUpdatedValue as string}`;
            }
            shareSettings.phone_icon_small = newUpdatedValue;
        } else if (fieldToUpdate === 'favicon') {
            // if it starts wtih /i/ we need to give it a full URL
            if (newUpdatedValue.startsWith('/i/') === true) {
                newUpdatedValue = `${window.location.origin}${newUpdatedValue as string}`;
            }
            shareSettings.favicon = newUpdatedValue;
        } else if (fieldToUpdate === 'google_tag') {
            shareSettings.google_tag = newUpdatedValue;
        }

        fieldToUpdate = 'share_settings';
        newUpdatedValue = shareSettings;

        await topicData.fetchAndUpdateTopicData({
            [fieldToUpdate]: newUpdatedValue
        }, this.amplitude_callsite, false, false);

        this.requestUpdate();
        topicData.mapComponent?.requestUpdate();
        await topicData.mapComponent?.pointCardPaginationEl?.firstUpdated();

        if (isDefined(e.detail.callback)) {
            await e.detail.callback();
        }
    }

    openUpgradeDrawer(e: CustomEvent) {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: e.detail.featureType
                },
            })
        );
    }

    private toggleSubfield(e: CustomEvent, settingList: SettingStructure[]) {
        // find title in settingsList as either fieldName or fieldname textand set isHidden to false if it is true and true if it is false
        const fieldTitle = e.detail.fieldTitle;
        const setting = settingList.find(setting => setting.fieldName === fieldTitle);

        if (isDefined(setting)) {
            if (e.detail.hidden === false) {
                setting.isHidden = false;
            } else {
                setting.isHidden = !(setting.isHidden ?? false);
            }
        }
        // update the new isHidden value in this.settingsList
        settingList = [...settingList];
    }


    private async changeMapPrivacy(level: DiscoveryLevel) {
        if (!featuresInfo.makeMapsPrivateEnabled && level === DiscoveryLevel.Private) {
            logWithAmplitude(this.amplitude_callsite, 'Upgrade Dialog Opened');
            const featureType = FeatureType.MakeMapsPrivate;
            this.openUpgradeDrawer(new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType,
                },
            }));
        } else {
            await topicData.fetchAndUpdateTopicData({
                discovery_level: level,
            }, this.amplitude_callsite, false, false);
            this.requestUpdate();
        }
    }

    render() {
        return html`
            <div class="style__wrapper">

                <div class="style__section">
                    <h4 class="sub-title">Public Visibility</h4>
                    <p class="info-paragraph">Public maps are findable by search engines. Private maps are only accessible by people with the link.
                        <span>
                            Your map is currently
                            <b>${topicData.discoveryLevel === DiscoveryLevel.Private ? 'Private' : topicData.discoveryLevel === DiscoveryLevel.ReadPublic ? 'Public (View Only)' : 'Public (Collaborative)'}</b>.
                        </span>
                    </p>
                    <br>
                    <proxi-selectfield
                        id="public-visibility"
                        outlined
                        value="${topicData.topic?.discovery_level ?? DiscoveryLevel.Private}"
                    >
                        ${topicData.discoveryLevel === DiscoveryLevel.Private ? html`
                            <mwc-list-item value="private">
                                Current Visibility: Private
                            </mwc-list-item>
                        ` : ''}
                        ${topicData.discoveryLevel === DiscoveryLevel.ReadPublic ? html`
                            <mwc-list-item value="read_public">
                                Current Visibility: Public (View Only)
                            </mwc-list-item>
                        ` : ''}
                        ${topicData.discoveryLevel === DiscoveryLevel.AddpointPublic ? html`
                            <mwc-list-item value="addpoint_public">
                                Current Visibility: Public (Collaborative)
                            </mwc-list-item>
                        ` : ''}
                        ${topicData.discoveryLevel !== DiscoveryLevel.Private ? html`
                            <mwc-list-item value="private" @click="${async () => await this.changeMapPrivacy(DiscoveryLevel.Private)}" title="Make your map private. Only people who visit where it is embedded or have the link can view or suggest points to it.">
                                <mwc-icon class="shareButtonIcon">public_off</mwc-icon>
                                <span>Change to Private</span>
                            </mwc-list-item>
                        ` : ''}
                        ${topicData.discoveryLevel !== DiscoveryLevel.ReadPublic ? html`
                            <mwc-list-item value="read_public" @click="${async () => await this.changeMapPrivacy(DiscoveryLevel.ReadPublic)}" title="It will be indexed on search engines. Viewers can only view the map.">
                                <mwc-icon class="shareButtonIcon">public</mwc-icon>
                                <span>Change to Public (View Only)</span>
                            </mwc-list-item>
                        ` : ''}
                        ${topicData.discoveryLevel !== DiscoveryLevel.AddpointPublic ? html`
                            <mwc-list-item value="addpoint_public" @click="${async () => await this.changeMapPrivacy(DiscoveryLevel.AddpointPublic)}" title="Viewers can add or suggest points and it will be indexed on search engines.">
                                <mwc-icon class="shareButtonIcon">public</mwc-icon>
                                <span>Change to Public (Collaborative)</span>
                            </mwc-list-item>
                        ` : ''}
                    </proxi-selectfield>
                    <br>
                    <hr class="divider" />

                    <h4 class="sub-title">SEO Settings</h4>
                    <p class="info-paragraph">These settings will be used when sharing the map on social media or search engines. Need help resizing your images?
                        Visit <a href="https://favicon.io/" target="_blank" rel="noopener noreferrer">https://favicon.io/</a> for a free online image resizer.</p>
                    </p>

                    ${this.shareSettingsList.map((setting) => {
                            return html`
                                <setting-component
                                    style="width:${setting.widthPercentage ?? 100}%"
                                    .setting_details=${setting}
                                    .is_hidden=${setting.isHidden ?? false}
                                    @on-change-value=${this.hasChange}
                                    @on-toggle-subfield=${(e: CustomEvent) => this.toggleSubfield(e, this.shareSettingsList)}
                                    @open-upgrade-dialog=${(e: CustomEvent) => this.openUpgradeDrawer(e)}
                                >
                                </setting-component>
                            `;
                        })}

                    <hr class="divider" />
                    <h4 class="sub-title">Analytics Settings</h4>
                    ${this.analyicsSettingsList.map((setting) => {
                            return html`
                                <setting-component
                                    style="width:${setting.widthPercentage ?? 100}%"
                                    .setting_details=${setting}
                                    .is_hidden=${setting.isHidden ?? false}
                                    @on-change-value=${this.hasChange}
                                    @on-toggle-subfield=${(e: CustomEvent) => this.toggleSubfield(e, this.shareSettingsList)}
                                    @open-upgrade-dialog=${(e: CustomEvent) => this.openUpgradeDrawer(e)}
                                >
                                </setting-component>
                            `;
                        })}

                    <hr class="divider" />

                    <custom-urls
                        @on-change-value=${this.onURLChange}
                        >
                    </custom-urls>

                </div>

            </div>
            </div>

        `;
    }

    static get styles() {
        return css`
            :host {
                flex: 1;
            }

            h3 {
                margin: .5rem;
            }

            .title {
                color: var(--main-primary-color);
                text-align: center;
                font-size: 1rem;
                font-weight: 700;
                line-height: 130%;
                margin: 5px 0;
                text-align: left;
            }

            .warning {
                border: 1px solid var(--main-invalid-color);
                margin-top: 0.5rem;
                margin-bottom: 0.5rem;
                padding: 0.5rem;
                background-color: var(--main-warning-color);
                font-size: 0.8rem;
            }

            .reset-button {
                cursor: pointer;
                text-decoration: underline;
            }

            .info-paragraph {
                font-size: 0.8rem;
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .sub-title {
                margin-bottom: 5px;
                margin-top: 5px;
                color: var(--theme-input-color);
            }

            .style__wrapper {
                height:100%;
                display: flex;
                flex-direction: column;
                justify-content: center;
            }

            .mapStyleCollection {
                display: grid;
                grid-template-columns: 1fr 1fr 1fr 1fr;
                grid-gap: 0.5rem;

            }

            .style__section {
                padding-right: 20px;
                flex: 1;
                overflow-y: auto;
                overflow-x: hidden;
            }

            .map-style-component__tabs {
                display: flex;
                justify-content: flex-start;
            }

            .map-style-component__tab {
                padding-bottom: 0.25rem;
                line-height: 1.5;
                color: var(--theme-lighter-text-color);
                cursor: pointer;
                margin-right: 8px;
                flex: 1;
                text-align: center;
            }

            .map-style-component__tab.active {
                font-weight: 700;
                border-bottom: 2px solid var(--main-secondary-color);
                color: var(--main-secondary-color);
            }

            .mapStyleItem {
                text-align: center;
            }

            .mapStyleItem.active {
                font-weight: 700;
            }

            .mapStyleItem img {
                width: 100%;
                height: 3.5rem;
                border-radius: 0.75rem;
                border: 2px solid transparent;
                box-shadow: 0px 2px 8px -4px rgba(31, 27, 45, 0.12);
            }

            .header-with-info {
                display: flex;
                flex-direction: row;
                align-items: center;
                margin-top: 5px;
                margin-bottom: 5px;
            }

            .mapStyleItem.active img {
                border: 2px solid var(--main-primary-color);
            }

            .icon__actions {
                padding-top: 18px;
                display: flex;
                justify-content: space-evenly;
            }
		    .input {
                width: 100%;
                padding: auto 5px;
            }

            .shareButtonIcon {
                --mdc-icon-size: 1rem;
            }

            .style__actions {
                display: flex;
                flex-direction: row;
                justify-content: flex-end;
                padding: 1rem 0.5rem;
                gap: 0.5rem;
            }
        `;
    }
}
